//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

/**
 * Types for the multi-group integration preference flow.
 *
 * A user may belong to several groups in one teamspace, each granting one integration.
 * Each group-backed path is a separate selectable option even when two groups point at
 * the same integration, so options are keyed by group, not by integration name.
 *
 * Mirrors contracts/integration-preferences.openapi.yaml.
 */

/** Why a given configuration won. TEAMSPACE_DEFAULT is retired — access is group-only. */
export type ConfigurationSource =
  | 'USER_OVERRIDE'
  | 'USER_SELECTED_DEFAULT'
  | 'GROUP_CONFIGURATION'
  | 'NO_ACCESS';

/** One integration the user can reach, via one specific group. */
export interface AccessibleIntegrationOption {
  integrationName: string;
  groupId: string;
  groupName: string;
  /** ISO-8601. Earliest membership wins when no explicit selection is saved. */
  membershipJoinedDate: string;
  isCurrentDefault: boolean;
  hasUserOverride: boolean;
  /** False when this group forbids personal overrides, evaluated per path. */
  allowUserOverride: boolean;
  /** Optional human-readable provenance for display. */
  sourceLabel?: string;
  /**
   * Whether the integration is enabled in the teamspace. Disabled paths are still listed —
   * hiding them makes a group the user belongs to look like it disappeared — but they grant
   * nothing and cannot be made the default.
   */
  isEnabled?: boolean;
  /** Provider name from the integration config, e.g. "OPENAI". Drives the provider icon. */
  provider?: string;
  /** How the integration authenticates; defaults to "API Key" server-side. */
  credentialType?: string;
}

/** The user's saved preferred path for one teamspace. */
export interface DefaultIntegrationSelection {
  teamspaceId: string;
  integrationName: string;
  selectedGroupId: string;
  selectedGroupName: string;
  /** ISO-8601. */
  updatedDate: string;
  /**
   * False when the saved selection no longer resolves — the user left the group, or the
   * integration was disabled or unassigned. The row is kept; resolution falls through.
   */
  valid: boolean;
}

/** Response of GET /users/me/integration-preferences. */
export interface IntegrationPreferences {
  currentDefault?: DefaultIntegrationSelection;
  options: AccessibleIntegrationOption[];
  /**
   * True only when the user must choose: two or more valid options and no valid saved
   * selection. With exactly one option the server auto-selects and this stays false, so
   * single-group users are never prompted.
   */
  requiresSelection: boolean;
  fallbackSource?: ConfigurationSource;
}

/** Request body of POST /users/me/default-integration. */
export interface UpdateDefaultIntegrationSelectionRequest {
  integrationName: string;
  selectedGroupId: string;
}

/**
 * Window event fired after the user's default integration is saved anywhere in the app, so
 * other views (Member Access) can refresh without a page reload.
 */
export const DEFAULT_INTEGRATION_CHANGED_EVENT = 'integration-default-changed';

/**
 * Announce that the saved default changed. `source` names the announcer, so a view that
 * already refreshed itself after its own save can ignore its own announcement rather than
 * fetching twice.
 */
export const announceDefaultIntegrationChanged = (source: string): void => {
  window.dispatchEvent(
    new CustomEvent(DEFAULT_INTEGRATION_CHANGED_EVENT, { detail: { source } }),
  );
};

/** Whether a changed-default event was announced by `source` itself. */
export const isOwnDefaultChange = (event: Event, source: string): boolean =>
  (event as CustomEvent<{ source?: string }>).detail?.source === source;

/**
 * Why a user has no usable integration, or null when they have one. Evaluated over the
 * options the preferences endpoint returns: disabled paths are listed there but grant
 * nothing, so they must not count as access.
 */
export type IntegrationAccessIssue = 'no_group' | 'all_disabled';

export const integrationAccessIssue = (
  options: ReadonlyArray<{ isEnabled?: boolean }>,
): IntegrationAccessIssue | null => {
  if (options.length === 0) return 'no_group';
  return options.some((o) => o.isEnabled !== false) ? null : 'all_disabled';
};
